import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DataTable } from '@/components/DataTable';
import { 
  Building2, Plus, Edit, Search, RefreshCw, Flame, Zap, Home, MapPin, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPES = ['Park Home', 'Static Caravan', 'Lodge', 'Bungalow', 'Communal', 'Commercial'];
const HEATING_TYPES = ['LPG', 'Mains Gas', 'Electric', 'Oil', 'None'];
const OCCUPANCY_TYPES = ['Owner Occupied', 'Rental', 'Commercial', 'Communal', 'Holiday Let'];
const AGE_BANDS = ['<10', '10-20', '20+'];
const RISK_LEVELS = ['Low', 'Medium', 'High'];

const HEATING_ICONS = {
  'LPG': <Flame className="h-4 w-4 text-orange-500" />,
  'Mains Gas': <Flame className="h-4 w-4 text-blue-500" />,
  'Electric': <Zap className="h-4 w-4 text-yellow-500" />,
  'Oil': <Flame className="h-4 w-4 text-amber-700" />,
  'None': <Home className="h-4 w-4 text-gray-400" />,
};

export default function AssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [heatingFilter, setHeatingFilter] = useState('all');
  const [occupancyFilter, setOccupancyFilter] = useState('all');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [assetsRes, parksRes] = await Promise.all([
        axios.get(`${API}/api/ppm-core/assets?limit=500&active_only=false`, { headers }),
        axios.get(`${API}/api/parks`, { headers }).catch(() => ({ data: [] })),
      ]);
      setAssets(assetsRes.data.assets || []);
      setParks(parksRes.data || []);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditAsset = (asset) => {
    setEditingAsset(asset ? { ...asset } : {
      park_name: '',
      address: '',
      pitch_or_unit: '',
      asset_type: 'Park Home',
      heating_type: 'LPG',
      has_boiler: true,
      occupancy_type: 'Owner Occupied',
      unit_age_band: null,
      risk_level: 'Low',
      region: '',
      active: true,
    });
    setShowEditDialog(true);
  };

  const handleSaveAsset = async () => {
    try {
      if (editingAsset.id) {
        await axios.put(`${API}/api/ppm-core/assets/${editingAsset.id}`, editingAsset, { headers });
        toast.success('Asset updated');
      } else {
        await axios.post(`${API}/api/ppm-core/assets`, editingAsset, { headers });
        toast.success('Asset created');
      }
      setShowEditDialog(false);
      setEditingAsset(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleParkSelect = (parkName) => {
    const park = parks.find(p => p.name === parkName);
    if (park) {
      setEditingAsset({
        ...editingAsset,
        park_name: park.name,
        address: park.address || '',
        region: park.region || '',
      });
    } else {
      setEditingAsset({ ...editingAsset, park_name: parkName });
    }
  };

  const filteredAssets = assets.filter(a => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (a.park_name || '').toLowerCase().includes(searchLower) ||
      (a.address || '').toLowerCase().includes(searchLower) ||
      (a.pitch_or_unit || '').toLowerCase().includes(searchLower);
    
    // Handle special rental filter from tile click
    if (typeFilter === '__rental__') {
      return matchesSearch && a.occupancy_type === 'Rental';
    }
    
    const matchesType = typeFilter === 'all' || a.asset_type === typeFilter;
    const matchesHeating = heatingFilter === 'all' || a.heating_type === heatingFilter;
    const matchesOccupancy = occupancyFilter === 'all' || a.occupancy_type === occupancyFilter;
    return matchesSearch && matchesType && matchesHeating && matchesOccupancy;
  });

  const columns = [
    {
      accessorKey: 'park_name',
      header: 'Park / Location',
      cell: ({ row }) => (
        <Link 
          to={`/assets/${row.original.id}`}
          className="block hover:text-[#1F7A78]"
        >
          <div className="font-medium flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-400" />
            {row.original.park_name}
          </div>
          <div className="text-xs text-slate-500 ml-6">
            {row.original.address}
          </div>
        </Link>
      )
    },
    {
      accessorKey: 'pitch_or_unit',
      header: 'Unit',
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.pitch_or_unit || '-'}</span>
      )
    },
    {
      accessorKey: 'asset_type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.asset_type}</Badge>
      )
    },
    {
      accessorKey: 'heating_type',
      header: 'Heating',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {HEATING_ICONS[row.original.heating_type] || <Home className="h-4 w-4" />}
          <span className="text-sm">{row.original.heating_type}</span>
        </div>
      )
    },
    {
      accessorKey: 'has_boiler',
      header: 'Boiler',
      cell: ({ row }) => (
        <Badge className={row.original.has_boiler ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
          {row.original.has_boiler ? 'Yes' : 'No'}
        </Badge>
      )
    },
    {
      accessorKey: 'occupancy_type',
      header: 'Occupancy',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.occupancy_type}</span>
      )
    },
    {
      accessorKey: 'risk_level',
      header: 'Risk',
      cell: ({ row }) => {
        const colors = {
          'Low': 'bg-green-100 text-green-700',
          'Medium': 'bg-yellow-100 text-yellow-700',
          'High': 'bg-red-100 text-red-700',
        };
        return (
          <Badge className={colors[row.original.risk_level] || 'bg-gray-100'}>
            {row.original.risk_level}
          </Badge>
        );
      }
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge className={row.original.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
          {row.original.active ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
  ];

  const getActions = (asset) => [
    {
      label: 'View',
      icon: Eye,
      onClick: () => navigate(`/assets/${asset.id}`),
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: () => handleEditAsset(asset),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F7A78]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Assets / Units</h1>
          <p className="text-slate-500">Manage park homes, units, and properties for PPM scheduling</p>
        </div>
        <Button onClick={() => handleEditAsset(null)} className="bg-[#1F7A78] hover:bg-[#16615f]">
          <Plus className="h-4 w-4 mr-2" />
          Add Asset
        </Button>
      </div>

      {/* Stats - Clickable Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className="cursor-pointer hover:opacity-90 hover:shadow-lg transition-all bg-[#1F7A78] border-none rounded-xl"
          onClick={() => { setTypeFilter('all'); setHeatingFilter('all'); setOccupancyFilter('all'); setSearchTerm(''); }}
          data-testid="tile-total-assets"
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{assets.length}</div>
            <div className="text-xs text-white/90 font-medium">Total Assets</div>
            <div className="text-[10px] text-white/70 mt-1">Click to show all</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:opacity-90 hover:shadow-lg transition-all border-none rounded-xl bg-[#1F7A78] ${heatingFilter === 'LPG' ? 'ring-2 ring-white' : ''}`}
          onClick={() => { setHeatingFilter(heatingFilter === 'LPG' ? 'all' : 'LPG'); setTypeFilter('all'); setOccupancyFilter('all'); }}
          data-testid="tile-lpg-units"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-white/80" />
              <div className="text-2xl font-bold text-white">{assets.filter(a => a.heating_type === 'LPG').length}</div>
            </div>
            <div className="text-xs text-white/90 font-medium">LPG Units</div>
            <div className="text-[10px] text-white/70 mt-1">{heatingFilter === 'LPG' ? 'Click to clear filter' : 'Click to filter'}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:opacity-90 hover:shadow-lg transition-all border-none rounded-xl bg-[#1F7A78] ${heatingFilter === 'Mains Gas' ? 'ring-2 ring-white' : ''}`}
          onClick={() => { setHeatingFilter(heatingFilter === 'Mains Gas' ? 'all' : 'Mains Gas'); setTypeFilter('all'); setOccupancyFilter('all'); }}
          data-testid="tile-mains-gas"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-white/80" />
              <div className="text-2xl font-bold text-white">{assets.filter(a => a.heating_type === 'Mains Gas').length}</div>
            </div>
            <div className="text-xs text-white/90 font-medium">Mains Gas</div>
            <div className="text-[10px] text-white/70 mt-1">{heatingFilter === 'Mains Gas' ? 'Click to clear filter' : 'Click to filter'}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:opacity-90 hover:shadow-lg transition-all border-none rounded-xl bg-[#1F7A78] ${heatingFilter === 'Electric' ? 'ring-2 ring-white' : ''}`}
          onClick={() => { setHeatingFilter(heatingFilter === 'Electric' ? 'all' : 'Electric'); setTypeFilter('all'); setOccupancyFilter('all'); }}
          data-testid="tile-electric"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-white/80" />
              <div className="text-2xl font-bold text-white">{assets.filter(a => a.heating_type === 'Electric').length}</div>
            </div>
            <div className="text-xs text-white/90 font-medium">Electric</div>
            <div className="text-[10px] text-white/70 mt-1">{heatingFilter === 'Electric' ? 'Click to clear filter' : 'Click to filter'}</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:opacity-90 hover:shadow-lg transition-all border-none rounded-xl bg-[#1F7A78] ${occupancyFilter === 'Rental' ? 'ring-2 ring-white' : ''}`}
          onClick={() => { 
            setOccupancyFilter(occupancyFilter === 'Rental' ? 'all' : 'Rental');
            setTypeFilter('all');
            setHeatingFilter('all');
          }}
          data-testid="tile-rental-units"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-white/80" />
              <div className="text-2xl font-bold text-white">{assets.filter(a => a.occupancy_type === 'Rental').length}</div>
            </div>
            <div className="text-xs text-white/90 font-medium">Rental Units</div>
            <div className="text-[10px] text-white/70 mt-1">{occupancyFilter === 'Rental' ? 'Click to clear filter' : 'Click to filter'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Search by park, address, or unit..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="asset-search-input"
            />
          </div>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]" data-testid="type-filter">
            <SelectValue placeholder="Asset Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ASSET_TYPES.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={heatingFilter} onValueChange={setHeatingFilter}>
          <SelectTrigger className="w-[150px]" data-testid="heating-filter">
            <SelectValue placeholder="Heating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Heating</SelectItem>
            {HEATING_TYPES.map(h => (
              <SelectItem key={h} value={h}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable 
            columns={columns} 
            data={filteredAssets}
            actions={getActions}
            isLoading={loading}
            emptyMessage="No assets found. Add your first asset to start PPM scheduling."
          />
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset?.id ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          </DialogHeader>
          {editingAsset && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Park Name *</Label>
                  <Input 
                    value={editingAsset.park_name || ''} 
                    onChange={(e) => setEditingAsset({...editingAsset, park_name: e.target.value})}
                    placeholder="Enter park name"
                    list="parks-list"
                    data-testid="edit-park-name"
                  />
                  <datalist id="parks-list">
                    {parks.map(p => (
                      <option key={p.id} value={p.name} />
                    ))}
                  </datalist>
                </div>
                <div className="col-span-2">
                  <Label>Address *</Label>
                  <Input 
                    value={editingAsset.address || ''} 
                    onChange={(e) => setEditingAsset({...editingAsset, address: e.target.value})}
                    data-testid="edit-address"
                  />
                </div>
                <div>
                  <Label>Pitch / Unit Number</Label>
                  <Input 
                    value={editingAsset.pitch_or_unit || ''} 
                    onChange={(e) => setEditingAsset({...editingAsset, pitch_or_unit: e.target.value})}
                    data-testid="edit-unit"
                  />
                </div>
                <div>
                  <Label>Region</Label>
                  <Input 
                    value={editingAsset.region || ''} 
                    onChange={(e) => setEditingAsset({...editingAsset, region: e.target.value})}
                    data-testid="edit-region"
                  />
                </div>
                <div>
                  <Label>Asset Type</Label>
                  <Select 
                    value={editingAsset.asset_type || 'Park Home'} 
                    onValueChange={(v) => setEditingAsset({...editingAsset, asset_type: v})}
                  >
                    <SelectTrigger data-testid="edit-asset-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Heating Type</Label>
                  <Select 
                    value={editingAsset.heating_type || 'LPG'} 
                    onValueChange={(v) => setEditingAsset({...editingAsset, heating_type: v})}
                  >
                    <SelectTrigger data-testid="edit-heating-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HEATING_TYPES.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Occupancy Type</Label>
                  <Select 
                    value={editingAsset.occupancy_type || 'Owner Occupied'} 
                    onValueChange={(v) => setEditingAsset({...editingAsset, occupancy_type: v})}
                  >
                    <SelectTrigger data-testid="edit-occupancy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCUPANCY_TYPES.map(o => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit Age Band</Label>
                  <Select 
                    value={editingAsset.unit_age_band || 'not-specified'} 
                    onValueChange={(v) => setEditingAsset({...editingAsset, unit_age_band: v === 'not-specified' ? null : v})}
                  >
                    <SelectTrigger data-testid="edit-age-band">
                      <SelectValue placeholder="Select age band" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Unknown</SelectItem>
                      {AGE_BANDS.map(a => (
                        <SelectItem key={a} value={a}>{a} years</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Risk Level</Label>
                  <Select 
                    value={editingAsset.risk_level || 'Low'} 
                    onValueChange={(v) => setEditingAsset({...editingAsset, risk_level: v})}
                  >
                    <SelectTrigger data-testid="edit-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RISK_LEVELS.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editingAsset.has_boiler !== false}
                    onCheckedChange={(v) => setEditingAsset({...editingAsset, has_boiler: v})}
                    data-testid="edit-has-boiler"
                  />
                  <Label className="text-sm">Has Boiler</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editingAsset.active !== false}
                    onCheckedChange={(v) => setEditingAsset({...editingAsset, active: v})}
                    data-testid="edit-active"
                  />
                  <Label className="text-sm">Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveAsset} className="bg-[#1F7A78] hover:bg-[#16615f]" data-testid="save-asset-btn">
              {editingAsset?.id ? 'Save Changes' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
